<template>
  <div class="vehicle-info modal-content-scrollable">
    <div v-if="loading" class="loading-container">
      <p>{{ $t('Loading vehicle information...') }}</p>
      <div class="spinner"></div>
    </div>

    <div v-else>
      <div class="vehicle-details">
        {{ $t('Vehicle') }}: {{ displayVehicleName }}
        <ul>
          <li>{{ $t('Plate') }}: {{ displayLicensePlate }}</li>
          <li>{{ $t('Type') }}: {{ selectedVehicleTypeName }}</li>
          <li>{{ $t('Unit') }}: {{ selectedUnitName }}</li>
          <li v-if="gps.speed !== undefined">
            {{ $t('Speed') }}: {{ gps.speed }} Km/h
          </li>
          <li v-if="gps.sentDate">
            {{ $t('Sent date') }}: {{ convertToLocalTime(gps.sentDate) }}
          </li>
        </ul>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="sensor-status">
            <b-button block variant="primary" size="sm" @click="toggleSensors">
              {{ showSensors ? $t("Hide Sensor Status") : $t("Show Sensor Status") }}
            </b-button>
            <div v-if="showSensors" class="text-center sensors-list">
              <ul>
                <li v-if="sensors.battery !== undefined">
                  {{ $t('Vehicle battery') }}<span :class="getStatusClass(sensors.battery)"></span>
                </li>
                <li v-if="hasOdometer && sensors.hood !== undefined">
                  {{ $t('Closed hood') }}<span :class="getStatusClass(sensors.hood)"></span>
                </li>
                <li v-if="sensors.communication !== undefined">
                  {{ $t('Communication') }}<span :class="getStatusClass(sensors.communication)"></span>
                </li>
                <li v-if="sensors.moving !== undefined">
                  {{ $t('Moving') }}<span :class="getStatusClass(sensors.moving)"></span>
                </li>
                <li v-if="hasOdometer && sensors.ignition !== undefined">
                  {{ $t('Ignition') }}<span :class="getStatusClass(sensors.ignition)"></span>
                </li>
                <li v-if="sensors.batteryLevel">
                  {{ $t('Battery level') }}: {{ sensors.batteryLevel }}
                </li>
                <li v-if="hasOdometer && sensors.driverDoor !== undefined">
                  {{ $t('Driver door closed') }}<span :class="getStatusClass(sensors.driverDoor)"></span>
                </li>
                <li v-if="hasOdometer && sensors.passengerDoor !== undefined">
                  {{ $t('Passenger door closed') }}<span :class="getStatusClass(sensors.passengerDoor)"></span>
                </li>
                <li v-if="sensors.gpsSignal !== undefined">
                  {{ $t('GPS signal') }}<span :class="getStatusClass(sensors.gpsSignal)"></span>
                </li>
                <li v-if="hasSpeed">
                  {{ $t('Speed over 100 km/h') }}<span :class="getStatusClass(sensors.speedOver100)"></span>
                </li>
                <li v-if="hasSpeed">
                  {{ $t('Speed over 60 km/h') }}<span :class="getStatusClass(sensors.speedOver60)"></span>
                </li>
                <li v-if="hasSpeed">
                  {{ $t('Speed over 80 km/h') }}<span :class="getStatusClass(sensors.speedOver80)"></span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="gps-info">
            <b-button block variant="primary" size="sm" @click="toggleGpsInfo">
              {{ showGpsInfo ? $t("Hide GPS info") : $t("Show GPS info") }}
            </b-button>
            <div v-if="showGpsInfo" class="gps-list">
              <ul>
                <li v-if="gps.position && gps.position !== '0,0'">
                  {{ $t('Position') }}: {{ gps.position }}
                </li>
                <li v-if="gps.date">
                  {{ $t('GPS date') }}: {{ convertToLocalTime(gps.date) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: "VehicleInfo",
  props: {
    vehicle: Object,
    vehicleDatalhe: Object,
  },
  data() {
    return {
      showSensors: false,
      showGpsInfo: false,
      vehicleTypes: {},
      units: {},
      lastUpdateData: null,
      vehicleDetalheLocal: this.vehicleDatalhe || {},
      loading: true,
    }
  },
  computed: {
    displayVehicleName() {
      const currentVehicle = this.vehicleDatalhe?.vehicle || this.vehicleDetalheLocal?.vehicle
      if (!currentVehicle) return 'Desconhecido'
      if (typeof currentVehicle === 'string') return currentVehicle
      if (typeof currentVehicle === 'object') return currentVehicle.name || JSON.stringify(currentVehicle)
      return 'Desconhecido'
    },
    displayLicensePlate() {
      return this.vehicleDatalhe?.vehicle?.license_plate
          ?? this.vehicleDetalheLocal?.license_plate
          ?? 'Desconhecida'
    },
    selectedVehicleTypeName() {
      const id = this.vehicleDetalheLocal?.vehicle?.vehicle_type_id
      return this.vehicleTypes[id] ?? this.vehicleDetalheLocal?.vehicleType ?? 'Desconhecido'
    },
    selectedUnitName() {
      const id = this.vehicleDetalheLocal?.vehicle?.unit_id ?? this.vehicle?.unit_id
      return this.units[id] ?? this.vehicleDetalheLocal?.unit ?? 'Desconhecido'
    },
    lastUpdateTime() {
      return this.lastUpdateData?.updated_at ?? null
    },
    gps() {
      if (!this.lastUpdateData && !this.vehicle) return {
        position: '0,0',
        date: '',
        sentDate: '',
        satellites: '',
        speed: 0,
        altitude: '',
        azimuth: ''
      }

      const latitude = this.vehicle?.lat || this.vehicle?.latitude || 0
      const longitude = this.vehicle?.lng || this.vehicle?.long || this.vehicle?.lon || 0
      const trackingDate = this.lastUpdateData?.last_tracking_at || this.lastUpdateData?.created_at || ''

      return {
        position: `${latitude},${longitude}`,
        date: trackingDate,
        sentDate: this.lastUpdateData?.updated_at || '',
        satellites: this.vehicle?.sats || '',
        speed: this.lastUpdateData?.speed || 0,
        altitude: this.vehicle?.altitude || '',
        azimuth: this.vehicle?.angle || ''
      }
    },
    sensors() {
      if (!this.lastUpdateTime || !this.lastUpdateData) return {
        battery: false,
        communication: false,
        gpsSignal: false,
        moving: false,
        batteryLevel: 'N/A'
      }

      const now = new Date()
      const lastUpdate = new Date(this.lastUpdateTime)
      const diffMins = (now - lastUpdate) / 60000
      const hasOdometer = this.lastUpdateData?.odometer != null

      return {
        battery: this.getVehicleBatteryStatus(),
        hood: this.getHoodStatus(),
        communication: diffMins <= 5,
        moving: this.getMovingStatus(),
        ignition: this.getIgnitionStatus(),
        batteryLevel: this.getMainBatteryDec(this.lastUpdateData?.device_battery_level),
        driverDoor: this.getDoorStatus('driver'),
        passengerDoor: this.getDoorStatus('passenger'),
        gpsSignal: this.getGpsSignalStatus(),
        speedOver100: this.getSpeedStatus(100),
        speedOver60: this.getSpeedStatus(60),
        speedOver80: this.getSpeedStatus(80)
      }
    },
    hasOdometer() {
      return this.lastUpdateData?.odometer != null
    },
    hasSpeed() {
      return this.lastUpdateData?.speed != null
    }
  },
  methods: {
    getVehicleBatteryStatus() {
      if (!this.vehicle?.VehicleBattery) return false
      return this.vehicle.VehicleBattery !== "ON"
    },
    getHoodStatus() {
      if (!this.hasOdometer) return undefined
      return this.lastUpdateData.hood_status === 0
    },
    getMovingStatus() {
      if (!this.lastUpdateData?.speed) return false
      return Number(this.lastUpdateData.speed) > 0
    },
    getIgnitionStatus() {
      if (!this.hasOdometer) return undefined
      return this.lastUpdateData.ignition_status === 0
    },
    getDoorStatus(type) {
      if (!this.hasOdometer) return undefined
      return this.lastUpdateData[`${type}_door_status`] === 0
    },
    getGpsSignalStatus() {
      if (!this.vehicle?.gps) return false
      return this.vehicle.gps !== "NOFIX"
    },
    getSpeedStatus(limit) {
      if (!this.hasSpeed) return undefined
      return Number(this.lastUpdateData.speed) > limit
    },
    getMainBatteryDec(val) {
      if (!val) return 'N/A'
      return val > 100 ? (val / 10).toFixed(1) : val
    },
    getStatusClass(status) {
      if (status === undefined) return ''
      return status ? 'status-green' : 'status-red'
    },
    toggleSensors() {
      this.showSensors = !this.showSensors
    },
    toggleGpsInfo() {
      this.showGpsInfo = !this.showGpsInfo
    },
    convertToLocalTime(dateString) {
      if (!dateString || typeof dateString !== 'string') return ''

      try {
        if (!dateString.includes('T') && !dateString.includes('Z')) {
          const [datePart, timePart] = dateString.split(' ')
          const [year, month, day] = datePart.split('-')
          const [hours, minutes, seconds] = timePart.split(':')
          return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
        }

        const dateObject = new Date(dateString)
        if (isNaN(dateObject.getTime())) return dateString

        const day = String(dateObject.getDate()).padStart(2, '0')
        const month = String(dateObject.getMonth() + 1).padStart(2, '0')
        const year = dateObject.getFullYear()
        const hours = String(dateObject.getHours()).padStart(2, '0')
        const minutes = String(dateObject.getMinutes()).padStart(2, '0')
        const seconds = String(dateObject.getSeconds()).padStart(2, '0')

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
      } catch (error) {
        return dateString
      }
    },
    async getVehicleTypes() {
      if (Object.keys(this.vehicleTypes).length) return
      try {
        const response = await axios.get(this.$store.state.api + 'vehicle_types')
        if (!response.data?.data) return
        response.data.data.forEach(vehicleType => this.$set(this.vehicleTypes, vehicleType.id, vehicleType.name))
      } catch (error) {
        console.error('Erro ao carregar vehicle types:', error)
      }
    },
    async getUnits() {
      if (Object.keys(this.units).length) return
      try {
        const response = await axios.get(this.$store.state.api + 'units')
        if (!response.data?.data) return
        response.data.data.forEach(unit => this.$set(this.units, unit.id, unit.name))
      } catch (error) {
        console.error('Erro ao carregar units:', error)
      }
    },
    async fetchVehicleById(vehicleId) {
      if (!vehicleId) return
      try {
        const response = await axios.get(`${this.$store.state.api}vehicles/${vehicleId}`)
        if (!response.data) return
        this.vehicleDetalheLocal = {
          ...this.vehicleDetalheLocal,
          vehicle: {
            ...this.vehicleDetalheLocal?.vehicle,
            ...response.data
          }
        }
      } catch (error) {
        console.error('Erro fetch vehicle by id:', error)
      }
    },
    async getLastUpdate(vehicleId) {
      if (!vehicleId) return
      try {
        const response = await axios.get(`${this.$store.state.api}vehicle_last_updates/vehicle/${vehicleId}`)
        this.lastUpdateData = response.data.data ?? response.data
      } catch (error) {
        console.error('Erro fetch last update:', error)
        this.lastUpdateData = null
      }
    }
  },
  watch: {
    'vehicleDatalhe.vehicle.id': {
      immediate: true,
      async handler(newId) {
        const vehicleId = newId ?? this.$route.params.id
        if (!vehicleId) return
        this.loading = true
        try {
          await Promise.all([
            this.getVehicleTypes(),
            this.getUnits(),
            this.fetchVehicleById(vehicleId),
            this.getLastUpdate(vehicleId)
          ])
        } finally {
          this.loading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.vehicle-info {
  font-family: Arial, sans-serif;
  font-size: 14px;
  border: 1px solid #ccc;
  padding: 12px 15px;
  border-radius: 6px;
  max-width: 480px;
  background-color: #fafafa;
  max-height: 600px;
  overflow-y: auto;
  color: #333;
}

.vehicle-details h3 {
  margin: 0 0 10px;
  font-size: 1.2em;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0 0 15px;
}

li {
  margin: 4px 0;
  font-size: 13px;
}

.status-green::before {
  content: "🟢";
  margin-right: 6px;
}

.status-red::before {
  content: "🔴";
  margin-right: 6px;
}

button {
  margin: 8px 0;
  padding: 6px 12px;
  font-size: 0.85em;
  cursor: pointer;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  user-select: none;
}

button:hover {
  background-color: #0056b3;
}

.loading-container {
  text-align: center;
  padding: 25px 0;
  font-weight: 600;
  font-size: 1em;
  color: #555;
}

h3 {
  color: #555;
}

.spinner {
  margin: 15px auto 0;
  width: 28px;
  height: 28px;
  border: 4px solid #007bff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
